import {
  FONT_FORMATTING_ATTRIBUTES,
  GJS_TYPE_ATTRIBUTE,
  MEANINGFUL_FORMATTING_STYLE_PROPS,
  PMW_ATTRIBUTE_PREFIX,
  RTE_JUNK_WRAPPER_SELECTOR,
} from '@EmailMakerReact/lib/email-maker-rte-content-normalize.types';

/**
 * Cleans the live innerHTML of a text component after a native RTE edit, so that GrapesJS'
 * `resetFromString` re-parses CLEAN markup (text + `<a>` + real formatting) into ONE text
 * component with inline link children — instead of fragmenting into many text components.
 *
 * The browser's `execCommand('createLink')` leaves behind empty, styling-only `<font>`/`<span>`
 * wrappers with no GrapesJS marker; on blur those get typed as standalone components. We strip
 * ONLY those junk wrappers here, before the re-parse, while preserving `<a>` (link presentation),
 * real formatting tags, any span that carries a PMW/GrapesJS marker (merge tags etc.), and any
 * `<font>`/`<span>` that carries meaningful per-run formatting (e.g. the color the `foreColor`
 * command applies) — dropping those would silently lose the user's formatting on every blur.
 *
 * Operates on a detached element only — never the live editor DOM.
 */
export function normalizeRteEditedHtml(html: string): string {
  const container = document.createElement('div');
  container.innerHTML = html;

  unwrapJunkWrappers(container);

  return container.innerHTML;
}

/**
 * Unwraps every junk `<font>`/`<span>` within `root`, innermost-first so nested wrappers collapse
 * fully in one pass.
 */
function unwrapJunkWrappers(root: HTMLElement): void {
  const wrappers = Array.from(root.querySelectorAll<HTMLElement>(RTE_JUNK_WRAPPER_SELECTOR));

  // Innermost-first: unwrapping a parent before its child would re-parent the child to the
  // grandparent, leaving the still-listed child detached and its unwrap a no-op.
  for (let i = wrappers.length - 1; i >= 0; i--) {
    const wrapper = wrappers[i];

    if (isUnwrappableWrapper(wrapper)) {
      unwrapElement(wrapper);
    }
  }
}

/**
 * A `<font>`/`<span>` is junk only when it carries neither a PMW/GrapesJS marker nor meaningful
 * per-run formatting. Marker-bearing wrappers (merge tags, PMW components) and formatting wrappers
 * (color, background, weight, style, decoration) are kept so the edit round-trips through the model.
 */
function isUnwrappableWrapper(el: HTMLElement): boolean {
  if (hasComponentMarker(el)) {
    return false;
  }

  return !carriesMeaningfulFormatting(el);
}

/** Whether the wrapper applies per-run text formatting (inline style, or a legacy `<font>` attribute). */
function carriesMeaningfulFormatting(el: HTMLElement): boolean {
  if (el.tagName === 'FONT' && FONT_FORMATTING_ATTRIBUTES.some((attr) => el.hasAttribute(attr))) {
    return true;
  }

  return MEANINGFUL_FORMATTING_STYLE_PROPS.some((prop) => el.style.getPropertyValue(prop) !== '');
}

/** Whether the element identifies as (part of) a PosterMyWall / GrapesJS component. */
function hasComponentMarker(el: HTMLElement): boolean {
  if (el.hasAttribute(GJS_TYPE_ATTRIBUTE)) {
    return true;
  }

  return el.getAttributeNames().some((name) => name.startsWith(PMW_ATTRIBUTE_PREFIX));
}

/** Replaces an element with its child nodes in place, then removes the now-empty element. */
export function unwrapElement(el: HTMLElement): void {
  const parent = el.parentNode;

  if (!parent) {
    return;
  }

  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el);
  }

  parent.removeChild(el);
}
